import {
  KahootRhythm,
  ArtistsGenres,
  Rhythm101,
  RhythmRally,
  RhythmPuzzle,
  HarmonyTheory,
  HarmonyGuesser,
  InstrumentFamilies,
  InstrumentListening,
  MelodyBasics,
  Melopedes,
  MusicalForm,
  Dynamics,
  PerformancePrep,
  GenericActivity,
  MyFavoriteMusicians,
  ChromeMusicLab,
  BeatDecoders,
  StudentSongs,
  PianoClicker,
  Harmonidome,
  InstrumentsInVideos,
  Incredibox,
  // KeySmashers, // Removed - no longer available
  TuneTesters,
  StepSkipRepeat,
  MelodyShaper
} from '../../../shared/assets/images';
import { default as ReactAttackTeacher } from '../../../shared/assets/images/games/ReactAttackTeacher.png';
import { default as RocketRace } from '../../../shared/assets/images/activities/rocketrace.png';
import { default as FlappyMuse } from '../../../shared/assets/images/games/flappy_muse.png';
import { default as SoundGym } from '../../../shared/assets/images/activities/soundgym.png';
import { default as ChordstructionImg } from '../../../shared/assets/images/games/Chordstruction Thumbnail.png';
import { default as ScaleTheTowerImg } from '../../../shared/assets/images/games/Scale The Tower Thumbnail.png';
import { default as TunaImg } from '../../../shared/assets/images/games/Tuna Thumbnail.png';
import { default as RealOrAiImg } from '../../../shared/assets/images/games/Real Or AI Thumbnail.png';
import { default as OvertureCertificate } from '../../../shared/assets/documents/Overture Certificate (1).png';
import { default as CreateParentAccountPdf } from '../../../shared/assets/documents/Create Parent Account Overture Games.pdf';
import { default as TrophySongly } from '../../../shared/assets/images/activities/trophy_songly.png';
import { default as FavoriteHits } from '../../../shared/assets/images/activities/favorite_hits.png';
import { default as YoutubeImage } from '../../../shared/assets/images/activities/youtube.png';
import { default as SunoImage } from '../../../shared/assets/images/activities/Suno_activity.png';
// import { default as MyFavoriteMusiciansGameImage } from '../../../shared/assets/images/games/my_favorite_musicians.png';
import { default as OvertureMusicLabImg } from '../../../shared/assets/images/games/Overture Music Lab Thumbnail.png';

export interface Activity {
  id: string;
  title: string;
  description: string;
  image: string;
  link?: string;
  instructions: string[];
  duration?: string;
  materials?: (string | { label: string; link: string })[];
  difficulty?: 'Easy' | 'Medium' | 'Hard';
}

export const activities: Activity[] = [
  // Temporary: Winter 2026 promo video (toggle via SHOW_WINTER_2026_PROMO in levels.ts)
  {
    id: 'winter-2026-promo',
    title: 'Winter Kickoff and Parent Accounts!',
    description: 'Kick off the quarter: distribute flyers and show the Winter Kickoff promo to help students understand Parent Accounts.',
    image: YoutubeImage,
    link: 'storage://videos/promotions/Winter Kickoff video promo.MOV',
    duration: '1 minute',
    difficulty: 'Easy',
    materials: [
      'Flyers (printed before class)',
      'Projector or shared screen',
      'Speakers',
      { label: 'Download: Create Parent Account (PDF)', link: CreateParentAccountPdf },
    ],
    instructions: [
      'Print flyers or have afterschool director print flyers before class',
      'Explain: "Tell your parents to make an account so they can see your progress and the music that you made"',
      'Play the promo video for the class -- make sure it is FULL SCREEN',
      'After the video make sure to ask: "What are you most excited to become this quarter?" (Hopefully an OG Creator)'
    ]
  },
  // Daily reflection activity (used by all levels)
  {
    id: 'reflect-and-progress',
    title: 'Reflection',
    description: 'Guide students to complete the daily reflection questions and reinforce key concepts.',
    image: TrophySongly,
    duration: '5-10 minutes',
    difficulty: 'Easy',
    instructions: [
      'Review today\'s main concepts with the class (2–3 minutes).',
      'Review the reflection questions shown in this mission with students.',
      'Students complete the reflection questions for today\'s level.',
      'Celebrate participation and highlight great insights!'
    ]
  },
  // Level 1 Activities - Updated Rhythm Content
  {
    id: 'kahoot-rhythm-basics',
    title: 'Kahoot: Introduction to Music',
    description: 'Interactive warm-up quiz to engage students and introduce program goals while gauging musical interests.',
    image: KahootRhythm,
    link: 'https://create.kahoot.it/share/introduction-to-overture-games/1465b222-0cf7-41ef-88be-01ada7ac3c8e',
    duration: '10 minutes',
    difficulty: 'Easy',
    materials: ['Student devices or shared screens', 'Projector for quiz display'],
    instructions: [
      'Project the Kahoot quiz on screen for all students to see',
      'Ensure each student has a device or group students together',
      'Start the 20-question quiz covering teacher intro, program goals, and student musical interests',
      'Discuss answers as you go to build engagement and excitement',
      'Use responses to understand student backgrounds and preferences',
      'Celebrate participation and set positive tone for the program'
    ]
  },
  {
    id: 'artists-genres-exploration',
    title: 'Artists and Genres Discovery (Legacy)',
    description: 'Students explore musical genres by sharing favorite songs and connecting them to musical elements.',
    image: ArtistsGenres,
    duration: '20 minutes',
    difficulty: 'Easy',
    materials: ['Flashcards or paper', 'Pens/pencils'],
    instructions: [
      'Give each student a flashcard and pen',
      'Have students write their name and three favorite songs with artists and genres',
      'Pair students to share their song lists and explain their choices',
      'Lead class discussion: "What song did you choose and why?"',
      'Ask: "What makes the song fit into that genre?" and "How does it make you feel?"',
      'Use student examples to explain rhythm, melody, harmony, tempo, and dynamics',
      'Collect flashcards to reference in future lessons'
    ]
  },
  {
    id: 'rhythm-101-basics',
    title: 'Rhythm 101: Movement and Beat',
    description: 'Physical introduction to rhythm through movement, teaching quarter notes and eighth notes with body percussion.',
    image: Rhythm101,
    duration: '10 minutes',
    difficulty: 'Easy',
    materials: ['Open space for movement', 'Simple 4/4 beat track or clapping'],
    instructions: [
      'Form a circle with all students',
      'Establish a steady beat by clapping or playing a track',
      'Teach commands: "Quarter Note!" = one slow step, "Eighth Notes!" = two quick steps',
      'Call out commands and have students respond with movement',
      'Start slow, then increase speed as students gain confidence',
      'Switch between quarter and eighth note commands randomly',
      'End with students clapping along to reinforce the beat'
    ]
  },
  {
    id: 'rhythm-rally-game',
    title: 'Rhythm Rally: Digital Challenge',
    description: 'Online rhythm game where students compete to maintain rhythm patterns and earn achievements.',
    image: RhythmRally,
    link: '/games/RhythmRally',
    duration: '10 minutes',
    difficulty: 'Medium',
    materials: ['Devices (tablets/computers)', '3 students max per device'],
    instructions: [
      'Navigate to Rhythm Rally on the Overture website',
      'Assign devices with 3 students or fewer per device',
      'Explain objective: keep the rhythm going as long as possible',
      'Let students compete to see who can maintain rhythm longest',
      'Encourage students to help each other and celebrate achievements',
      'Monitor progress and provide encouragement for struggling students'
    ]
  },
  {
    id: 'rhythm-puzzle-challenges',
    title: 'Rhythm Puzzle: Digital Challenge',
    description: 'Interactive digital treasure hunt where students crack rhythm codes to unlock virtual locks and reveal the treasure!',
    image: RhythmPuzzle,
    link: '/games/RhythmPuzzle',
    duration: '15 minutes',
    difficulty: 'Medium',
    materials: ['Projector or shared screen', 'Teacher device for controls'],
    instructions: [
      'Navigate to the Rhythm Puzzle game and project it for all students to see',
      'Explain: "We need to crack 3 rhythm codes to unlock the treasure chest!"',
      'Click "Next Code" to reveal each rhythm pattern on screen',
      'Have students clap the rhythm pattern together as a class',
      'When students perform the rhythm correctly, click the corresponding "Unlock Lock" button',
      'Celebrate as each lock disappears and cheer when the treasure chest opens with confetti!',
      'Use the "Reset Game" button to play again with different patterns'
    ]
  },
  {
    id: 'beat-making-intro',
    title: 'Intro to Beat Making with Overture Music Lab',
    description: 'Interactive introduction to beat making using Overture Music Lab, teaching students the basics of rhythm and melody composition.',
    image: OvertureMusicLabImg,
    link: '/games/OvertureMusicLab/index.html',
    duration: '15 minutes',
    difficulty: 'Easy',
    materials: ['Computers/tablets with internet access', 'Projector for demonstration'],
    instructions: [
      'Project Overture Music Lab on screen for all students to see',
      'Explain: "We\'re going to create our own beats using this cool music tool!"',
      'Demonstrate the basic interface: clicking squares to create rhythm patterns',
      'Show students how to select different drum sounds (kick, snare, etc.)',
      'Create a simple 4-beat pattern together as a class',
      'Have students access the tool on their devices and create their own 8-beat patterns',
      'Encourage experimentation with different drum sounds and rhythms',
      'Share and celebrate student creations by playing them for the class'
    ]
  },
  {
    id: 'beat-decoders-challenge',
    title: 'Beat Decoders: Performance & Creation',
    description: 'Students play through Beat Decoders levels, learning to perform, modify, and create their own beats while earning achievements.',
    image: BeatDecoders,
    link: '/games/BeatDecoders3.0',
    duration: '20 minutes',
    difficulty: 'Medium',
    materials: ['Computers/tablets with internet access', 'Individual student accounts'],
    instructions: [
      'Have students login and navigate to Beat Decoders',
      'Explain: "Each level is an achievement!" Help them complete as many levels as possible',
      'Guide them through performing, modifying, and creating their own beats',
      'Encourage experimentation with different combinations and sounds',
      'Direct students to the songs tab to name each beat they created',
      'Celebrate their creations and achievements earned!'
    ]
  },
  {
    id: 'rhythm-simon-says',
    title: 'Rhythm Simon Says: Call and Response',
    description: 'Interactive rhythm warm-up where teacher leads call-and-response patterns, then students create and perform their own beats.',
    image: Rhythm101,
    duration: '10 minutes',
    difficulty: 'Easy',
    materials: ['Open space for movement', 'No additional materials needed'],
    instructions: [
      'Gather students in a circle or facing forward',
      'Explain: "We\'re playing Simon Says! Copy my beat exactly!"',
      'Clap, stomp, or slap short rhythm patterns and have students repeat them back',
      'Start with simple 4-beat patterns, then gradually increase complexity',
      'After 3-10 rounds, invite students to create their own short beats',
      'Have each comfortable student perform their beat for the class to repeat back'
    ]
  },
  {
    id: 'student-song-showcase',
    title: 'Showcase: Student Creations',
    description: "Celebrate and share student-created songs from the dashboard. A perfect way to highlight student work and have a class listening party.",
    image: StudentSongs,
    duration: '10-15 minutes',
    difficulty: 'Easy',
    materials: ['Projector or large screen for dashboard view', 'Class speakers'],
    instructions: [
      'From your Squad Dashboard, announce that it\'s time for a listening party!',
      'Explain: "We\'re going to listen to the awesome music you\'ve created in our games."',
      'Click on a student\'s muse from the attendance grid.',
      'Navigate to the "Songs Created" section at the top of their profile.',
      'Click the play button to share their song with the class.',
      'Lead applause and encourage students to share positive feedback with their peers.',
      'Ask for other volunteers who would like to share their creations.'
    ]
  },
  {
    id: 'rocket-race-rhythm-feel',
    title: 'Rocket Race: Straight vs. Swung',
    description: 'Explore the difference between straight and swung eighth notes in this high-speed racing game.',
    image: RocketRace,
    link: '/games/RocketRace',
    duration: '10-15 minutes',
    difficulty: 'Medium',
    materials: ['Projector or shared screen', 'Speakers'],
    instructions: [
      'Project the game Rocket Race for the class to see.',
      'Explain the concept of "rhythm feel": even straight eighth notes vs. long-short swung eighth notes.',
      'Demonstrate by clapping or vocalizing a straight rhythm (e.g., "ti-ti ti-ti") vs. a swung rhythm (e.g., "doo-bah doo-bah").',
      'Have the class call out the correct answer.',
      'Make sure the level of competition is appropriate for the level of the students.'
    ]
  },

  // Level 3 Activities
  {
    id: 'scale-the-tower-game',
    title: 'Scale the Tower',
    description: 'Climb a perilous tower one note at a time in this aural-skills challenge.',
    image: ScaleTheTowerImg,
    link: '/games/ScaleTheTower/index.html',
    duration: '10 minutes',
    difficulty: 'Easy',
    materials: ['Computers/tablets with internet access'],
    instructions: [
      'Have students open Scale the Tower.',
      'Explain: Listen carefully and move only when the pitch matches!',
      'Play until completing at least one successful climb.'
    ]
  },
  {
    id: 'harmony-guesser-classroom',
    title: 'Harmony Guesser: Team Challenge',
    description: 'A teacher-led classroom game where students work in teams to identify major (happy) and minor (sad) chords.',
    image: HarmonyGuesser,
    link: '/games/HarmonyGuesser',
    duration: '15 minutes',
    difficulty: 'Easy',
    materials: ['Projector or shared screen', 'Speakers'],
    instructions: [
      'Project the Harmony Guesser game for the class to see.',
      'Divide the class into two teams.',
      'Explain that they will hear a chord and must decide if it sounds "happy" (major) or "sad" (minor).',
      'Play a chord and have teams discuss their answer.',
      'Ask one team for their answer, then see if the other team agrees or disagrees.',
      'Reveal the correct answer and award points.',
      'For older or more competitive classes, keep score to determine a winner.'
    ]
  },
  {
    id: 'chordstruction-game',
    title: 'Chordstruction',
    description: 'Build towers by identifying chords correctly. Practice basic chord recognition.',
    image: ChordstructionImg,
    link: '/games/Chordstruction/index.html',
    duration: '10 minutes',
    difficulty: 'Easy',
    materials: ['Computers/tablets with internet access', 'Individual student accounts'],
    instructions: [
      'Open the Chordstruction game.',
      'Explain the objective: pick the correct chord to build the tower.',
      'Students play until they earn 1 achievement.'
    ]
  },
  {
    id: 'tuna-game',
    title: 'Tuna: Pitch Matching',
    description: 'Use your voice to match the target pitch and navigate obstacles. A fun aural-skills break that builds pitch accuracy.',
    image: TunaImg,
    link: '/games/Tuna/index.html',
    duration: '10 minutes',
    difficulty: 'Easy',
    materials: ['Computers/tablets with microphone access', 'Speakers'],
    instructions: [
      'Ensure the device microphone is enabled and working.',
      'Open the Tuna game and explain: “Match the target pitch with your voice to move safely!”.',
      'Have students take turns or play in pairs, focusing on steady, accurate pitch.',
      'Play for one run or until students complete a section; celebrate improvements in matching the pitch.'
    ]
  },
  {
    id: 'yeet-the-scoop-game',
    title: 'Yeet the Scoop',
    description: 'Deliver ice cream by identifying major and minor chords in this fun, fast-paced game. Learn the notes that make up each chord type.',
    image: GenericActivity,
    link: '/games/YeetTheScoop',
    duration: '10-15 minutes',
    difficulty: 'Medium',
    materials: ['Computers/tablets with internet access', 'Individual student accounts'],
    instructions: [
      'Have students log in and navigate to the "Yeet the Scoop" game.',
      'Explain the objective: Listen to the chord and deliver the correct ice cream cone (major or minor).',
      'Instruct students to play until they have successfully delivered 5 ice cream cones.',
      'Encourage them to pay attention to the notes shown for each chord to start recognizing the patterns.',
      'Monitor student progress and offer help if needed.'
    ]
  },
  {
    id: 'piano-clicker-game',
    title: 'Piano Clicker',
    description: 'Play piano, earn cash, and unlock upgrades while learning about intervals, chords, and scales.',
    image: PianoClicker,
    link: '/games/PianoClicker',
    duration: '15-20 minutes',
    difficulty: 'Easy',
    materials: ['Computers/tablets with internet access', 'Individual student accounts'],
    instructions: [
      'Direct students to open the Piano Clicker game.',
      'Encourage them to explore the game freely.',
      'The goal is to click the piano to earn cash and purchase upgrades.',
      'Point out how the upgrades relate to musical concepts like intervals, chords, and scales.',
      'Challenge them to unlock as many items as they can in the time allotted.'
    ]
  },
  {
    id: 'rocket-race-harmony',
    title: 'Rocket Race: Harmony Challenge',
    description: 'Race your rocket by correctly identifying musical concepts like Major/Minor chords or intervals.',
    image: RocketRace,
    link: '/games/RocketRace',
    duration: '10-15 minutes',
    difficulty: 'Medium',
    materials: ['Projector or shared screen', 'Speakers'],
    instructions: [
      'Project the game Rocket Race for the class to see.',
      'Instruct students to select the modes related to harmony, such as "Major vs. Minor" or "What\'s that Interval?".',
      'Explain that they need to listen carefully and have the class call out the correct answer to make the rocket go faster.',
      'Encourage them to try to beat their own high score or compete with another class.'
    ]
  },

  // Level 2 Activities
  {
    id: 'harmony-guesser',
    title: 'Harmony Guesser',
    description: 'Introduction to basic harmony concepts including chords, intervals, and consonance/dissonance.',
    image: HarmonyTheory,
    duration: '25-30 minutes',
    difficulty: 'Medium',
    materials: ['Piano/keyboard', 'Chord charts', 'Audio examples'],
    instructions: [
      'Introduce the concept of harmony vs. melody',
      'Play examples of major and minor chords on piano',
      'Teach the difference between consonant and dissonant sounds',
      'Have students sing simple harmonies together',
      'Practice identifying major vs. minor chords by ear',
      'Create simple chord progressions as a class'
    ]
  },
  {
    id: 'harmony-guesser-game',
    title: 'Harmony Guesser Game',
    description: 'Interactive game where students identify chord progressions and harmonic patterns.',
    image: HarmonyGuesser,
    link: '/games/HarmonyGuesser',
    duration: '15-20 minutes',
    difficulty: 'Medium',
    materials: ['Computers/tablets', 'Headphones'],
    instructions: [
      'Access the Harmony Guesser game',
      'Explain the objective: identify chord types and progressions',
      'Start with easier levels focusing on major/minor identification',
      'Progress to more complex chord progressions',
      'Track student scores and celebrate improvements',
      'Discuss challenging progressions together'
    ]
  },

  // Level 3 Activities
  {
    id: 'instrument-family-overview',
    title: 'Instrument Family Overview',
    description: 'Comprehensive introduction to the four main instrument families: strings, woodwinds, brass, and percussion.',
    image: InstrumentFamilies,
    duration: '30-35 minutes',
    difficulty: 'Easy',
    materials: ['Instrument pictures', 'Audio recordings', 'Family sorting cards'],
    instructions: [
      'Introduce the four instrument families',
      'Show pictures and play audio of each family',
      'Discuss how each family produces sound',
      'Have students sort instrument cards by family',
      'Play "instrument family charades" game',
      'Create a class chart of instrument families'
    ]
  },
  {
    id: 'instrument-listening-exercise',
    title: 'Instrument Listening Exercise',
    description: 'Focused listening activity to identify different instruments by their unique sounds and timbres.',
    image: InstrumentListening,
    duration: '20-25 minutes',
    difficulty: 'Medium',
    materials: ['Audio recordings', 'Listening worksheets', 'Instrument pictures'],
    instructions: [
      'Prepare audio clips of solo instruments',
      'Play each clip without showing the instrument',
      'Students write down what they think they hear',
      'Reveal the correct instrument and discuss',
      'Focus on timbre and unique sound qualities',
      'Challenge: Mix two instruments and identify both'
    ]
  },

  // Level 4 Activities
  {
    id: 'melody-pattern-basics',
    title: 'Melody Pattern Basics',
    description: 'Introduction to melodic concepts including pitch direction, intervals, and simple melody creation.',
    image: MelodyBasics,
    duration: '25-30 minutes',
    difficulty: 'Medium',
    materials: ['Piano/keyboard', 'Staff paper', 'Melody cards'],
    instructions: [
      'Explain melody as a sequence of pitches',
      'Demonstrate high and low pitches on piano',
      'Teach ascending and descending patterns',
      'Have students create hand gestures for pitch direction',
      'Practice singing simple melodies together',
      'Create class melodies using only 3-5 notes'
    ]
  },
  {
    id: 'melopedes-puzzle-game',
    title: 'Melopedes Puzzle Game',
    description: 'Creative puzzle game where students arrange musical segments to create complete melodies.',
    image: Melopedes,
    link: '/games/Melopedes',
    duration: '15-20 minutes',
    difficulty: 'Medium',
    materials: ['Computers/tablets'],
    instructions: [
      'Launch the Melopedes game',
      'Explain the puzzle objective: arrange melody segments',
      'Demonstrate how to drag and drop musical pieces',
      'Students work through puzzles individually',
      'Assist with challenging levels',
      'Celebrate completed puzzles and discuss melody patterns'
    ]
  },

  // Level 5 Activities
  {
    id: 'musical-form-introduction',
    title: 'Musical Form Introduction',
    description: 'Learn about song structure including verse, chorus, bridge, and other musical forms.',
    image: MusicalForm,
    duration: '20-25 minutes',
    difficulty: 'Medium',
    materials: ['Popular song examples', 'Form diagrams', 'Colored blocks'],
    instructions: [
      'Introduce basic song forms: verse, chorus, bridge',
      'Use colored blocks to represent different sections',
      'Listen to familiar songs and identify sections',
      'Map out the form of 2-3 popular songs',
      'Discuss why songs have repeating sections',
      'Create a simple AABA form as a class'
    ]
  },

  // Level 9 Activities
  {
    id: 'what-is-musical-form-9',
    title: 'Warm-Up: What Is Musical Form?',
    description: 'Help students recognize how musical sections create structure in songs.',
    image: Rhythm101,
    duration: '10 minutes',
    difficulty: 'Easy',
    materials: ['A song with clear A & B sections'],
    instructions: [
      'Play a short song with clear A & B sections.',
      'Ask students to raise their hands when they hear a new section.',
      'Discuss how we know when a section changes and why form is important in music.',
      'Play a song with repeated sections and have students label them (e.g., A, B, A).',
    ]
  },
  {
    id: 'react-attack-form-9',
    title: 'React Attack: Form',
    description: 'Play React Attack to teach form by identifying section changes and assigning memes to sections.',
    image: ReactAttackTeacher,
    link: '/games/ReactAttackStudent',
    duration: '15-20 minutes',
    difficulty: 'Medium',
    materials: ['Student devices for student version', 'Teacher device for teacher version'],
    instructions: [
      'Students open the student version of React Attack, teachers open the teacher version.',
      'Play the game to teach form, first by identifying when the form changes.',
      'Then, by assigning memes to the form sections to define clear moods.',
    ]
  },
  {
    id: 'chrome-music-lab-form-9',
    title: 'Overture Music Lab: ABA Form',
    description: 'Create a song with multiple distinct sections (ABA form) using Overture Music Lab.',
    image: OvertureMusicLabImg,
    link: '/games/OvertureMusicLab/index.html',
    duration: '15 minutes',
    difficulty: 'Easy',
    materials: ['Computers/tablets with internet access'],
    instructions: [
      'Navigate to Overture Music Lab.',
      'Create a song with at least two distinct sections (A and B).',
      'Arrange the sections in an ABA form.',
      'Share your creation with the class.',
    ]
  },
  {
    id: 'beat-decoders-form-9',
    title: 'Beat Decoders: Sectional Beats',
    description: 'Create a beat with two distinct sections in Beat Decoders.',
    image: BeatDecoders,
    link: '/games/BeatDecoders3.0',
    duration: '15 minutes',
    difficulty: 'Medium',
    materials: ['Computers/tablets with internet access', 'Individual student accounts'],
    instructions: [
        'Navigate to Beat Decoders.',
        'Create a beat that has two different-sounding sections.',
        'Think about making a verse and a chorus for your beat.',
        'Earn at least 2 achievements.'
    ]
  },
  {
    id: 'melopedes-form-9',
    title: 'Melopedes: Sectional Melodies',
    description: 'Create a melody with two distinct sections in Melopedes.',
    image: Melopedes,
    link: '/games/Melopedes',
    duration: '15 minutes',
    difficulty: 'Medium',
    materials: ['Computers/tablets with internet access', 'Individual student accounts'],
    instructions: [
        'Navigate to Melopedes.',
        'Create a melody that has two different-sounding sections.',
        'Use different bugs or patterns for each section.',
        'Earn at least 2 achievements.'
    ]
  },

  // Level 6 Activities
  {
    id: 'dynamics-lesson',
    title: 'Dynamics and Volume Control',
    description: 'Explore musical dynamics from pianissimo to fortissimo through interactive exercises.',
    image: Dynamics,
    duration: '20-25 minutes',
    difficulty: 'Easy',
    materials: ['Dynamic symbols cards', 'Music with clear dynamics'],
    instructions: [
      'Introduce dynamic markings: p, mp, mf, f',
      'Practice singing at different volume levels',
      'Use hand gestures to indicate dynamics',
      'Listen to classical music with clear dynamic changes',
      'Play "dynamics follow the leader" game',
      'Create a class song with dynamic changes'
    ]
  },

  // Level 7 Activities
  {
    id: 'performance-preparation',
    title: 'Performance Preparation Workshop',
    description: 'Prepare students for musical performance through practice techniques and confidence-building exercises.',
    image: PerformancePrep,
    duration: '30-35 minutes',
    difficulty: 'Medium',
    materials: ['Performance space', 'Music stands', 'Recording device (optional)'],
    instructions: [
      'Discuss performance anxiety and coping strategies',
      'Practice proper posture and breathing',
      'Rehearse entering and exiting the performance space',
      'Work on eye contact and stage presence',
      'Practice starting and ending together',
      'Do a mock performance with peer feedback'
    ]
  },

  // Default/Generic Activities
  {
    id: 'generic-completion',
    title: 'Classroom Activity',
    description: 'Complete this hands-on learning activity with your class.',
    image: GenericActivity,
    duration: '15-20 minutes',
    difficulty: 'Medium',
    materials: ['Varies by activity'],
    instructions: [
      'Review the activity objectives with students',
      'Explain the goal and expectations clearly',
      'Provide necessary materials and resources',
      'Monitor student progress and provide guidance',
      'Facilitate discussion and reflection',
      'Mark as complete when objectives are met'
    ]
  },
  {
    id: 'my-favorite-musicians-game',
    title: 'My Favorite Musicians',
    description: 'Interactive typing game where students share their musical taste by adding favorite artists and genres.',
    image: MyFavoriteMusicians,
    link: '/games/MyFavoriteMusicians',
    duration: '10-15 minutes',
    difficulty: 'Easy',
    materials: ['Computers/tablets', 'Individual student accounts'],
    instructions: [
      'Access the My Favorite Musicians game on student devices',
      'Explain the objective: add 5 artists or genres you love, ranked by preference',
      'Show students they can click popular cards or type their own choices',
      'Encourage students to drag items to reorder by preference',
      'Celebrate achievements as students unlock them',
      'Use student choices to start discussions about musical genres and preferences'
    ]
  },
  {
    id: 'harmonidome-game',
    title: 'Harmonidome: Class Composer',
    description: "Work together as a class to create a song! In this March Madness-style bracket, students vote on their favorite harmonies, and then get to create their own versions.",
    image: Harmonidome,
    link: '/games/HarmonyVotersTeacher',
    duration: '20-25 minutes',
    difficulty: 'Medium',
    materials: ['Projector for teacher view', 'Student devices with internet access'],
    instructions: [
      'Teacher: Open the Harmony Voters Teacher version on the projector.',
      'Students: Log in to your accounts and open the Harmony Voters Student version.',
      'Teacher: Provide the class code for students to join the session.',
      'Teacher: Play each harmony pairing. Students will vote on their favorite.',
      'Continue through the bracket until a class song is created!',
      'Teacher: Save the class song and give it a name with input from the students.',
      'After the class song is saved, each student will have the opportunity to create and save their own song.',
      'Students: Name your new creation in your "Songs" tab, just like in Beat Decoders!'
    ]
  },
  {
    id: 'instrument-families-exploration',
    title: 'Instrument Families',
    description: 'Explore the five main instrument families: brass, strings, woodwinds, percussion, and keyboards.',
    image: InstrumentFamilies,
    link: 'https://virtualpiano.net/virtual-music-instruments/',
    duration: '20-25 minutes',
    difficulty: 'Easy',
    materials: ['Computer with internet access', 'Speakers', 'Optional: a real instrument'],
    instructions: [
      'Introduce the 5 instrument families: Brass, Strings, Woodwinds, Percussion, and Keyboards.',
      'Use the link to let students explore virtual instruments from each family.',
      'Discuss the sounds and characteristics of each family.',
      'If available, demonstrate a real instrument for the class.'
    ]
  },
  {
    id: 'instruments-in-pop-music',
    title: 'Instruments in Videos',
    description: 'Listen to a playlist of popular music and identify the instruments being played.',
    image: InstrumentsInVideos,
    link: 'https://www.youtube.com/watch?v=5FCf7gEt3W8&list=PL-Kt6snleEJ9yCFxru8UgHSxlCbRFnW6L',
    duration: '15-20 minutes',
    difficulty: 'Medium',
    materials: ['Computer with internet access', 'Speakers', 'Whiteboard or paper for listing instruments'],
    instructions: [
      'Use the YouTube playlist as a resource for instrument spotting.',
      'Skip through videos to find and identify as many different instruments as you can.',
      'This is an active listening exercise, not passive watching. Keep it fast-paced!',
      'List the identified instruments and their families on a whiteboard.'
    ]
  },
  {
    id: 'incredibox-creation',
    title: 'Incredibox',
    description: 'Create your own music with a cast of animated beatboxers.',
    image: Incredibox,
    link: 'https://www.incredibox.com/demo/',
    duration: '15-20 minutes',
    difficulty: 'Easy',
    materials: ['Computers/tablets with internet access', 'Headphones recommended'],
    instructions: [
      'Direct students to the Incredibox demo.',
      'Encourage them to experiment by dragging and dropping icons onto the characters.',
      'Let students explore different combinations to create their own unique songs.',
      'Have students share their favorite creations with a partner or the class.'
    ]
  },
  {
    id: 'rocket-race-instruments-easy',
    title: 'Rocket Race: Instruments (Easy)',
    description: 'Identify instruments in a fun, fast-paced racing game.',
    image: RocketRace,
    link: '/games/RocketRace',
    duration: '10-15 minutes',
    difficulty: 'Easy',
    materials: ['Projector or shared screen', 'Speakers'],
    instructions: [
      'Project the game Rocket Race for the class to see.',
      'Select the easy instrument identification mode.',
      'Explain that the class needs to listen carefully and call out the correct instrument to make the rocket go faster.',
      'Encourage the class to try to beat their own high score.'
    ]
  },
  {
    id: 'rocket-race-instruments-hard',
    title: 'Rocket Race: Instruments (Hard)',
    description: 'Test your instrument identification skills in a challenging, high-speed racing game.',
    image: RocketRace,
    link: '/games/RocketRace',
    duration: '10-15 minutes',
    difficulty: 'Hard',
    materials: ['Projector or shared screen', 'Speakers'],
    instructions: [
      'Project the game Rocket Race for the class to see.',
      'Select the hard instrument identification mode.',
      'This mode will feature more instruments and require faster recognition.',
      'Challenge the class to beat their previous high scores.'
    ]
  },
  {
    id: 'beat-decoders-instrument-focus',
    title: 'Beat Decoders (Instrument Focus)',
    description: 'Focus on the rhythmic instruments in Beat Decoders while performing, modifying, and creating beats.',
    image: BeatDecoders,
    link: '/games/BeatDecoders3.0',
    duration: '20 minutes',
    difficulty: 'Medium',
    materials: ['Computers/tablets with internet access', 'Individual student accounts'],
    instructions: [
      'Have students login and navigate to Beat Decoders.',
      'As they play, draw their attention to the different drum and percussion sounds.',
      'Discuss how these rhythm instruments are used to build the foundation of a beat.',
      'Encourage them to create new beats focusing on interesting rhythmic instrument combinations.'
    ]
  },
  {
    id: 'tune-testers-mission',
    title: 'Tune Testers: Melody vs. Rhythm',
    description: 'Use our new game, Tune Testers, to help students distinguish between melody, rhythm, and random noise!',
    image: TuneTesters,
    link: '/games/MelodyDiscovery/',
    duration: '15 minutes',
    difficulty: 'Easy',
    materials: ['Projector or shared screen', 'Speakers'],
    instructions: [
      'Project the "Tune Testers" game for the class to see.',
      'Explain the goal: "We\'re going to be Tune Testers! Your job is to listen carefully and decide what you hear."',
      'Start with the "Melody vs. Random" mode. Play a few rounds as a class, having students call out their answers.',
      'Discuss why one is a melody (it follows a pattern) and the other is just random notes.',
      'Switch to the "Melody vs. Rhythm" mode. Play a few rounds and have the class clap back both examples.',
      'Discuss the difference: Melody has different pitches (high and low), while rhythm is about the timing of notes.'
    ]
  },
  {
    id: 'step-skip-repeat-game',
    title: 'The "Step, Skip, or Repeat" Game',
    description: 'A fun, energetic game where students identify melodic movement by performing physical actions.',
    image: StepSkipRepeat,
    link: '/games/StepSkipRepeat/',
    duration: '10 minutes',
    difficulty: 'Easy',
    materials: ['Projector or shared screen', 'Speakers', 'Student devices (1 per pair)'],
    instructions: [
      'Project the game and play one or two rounds as a whole class to demonstrate.',
      'Explain the on-screen instructions: STAND UP for a step, JUMP for a skip, and SIT DOWN for a repeat.',
      'Have students grab a partner and find one device to share.',
      'Instruct pairs to play the game together on one student\'s account.',
      'The goal is for the pair to earn at least one achievement together.'
    ]
  },
  {
    id: 'melody-shaper-game',
    title: 'Melody Shaper Game',
    description: 'Help students visualize the contour of a melody by drawing its shape in this interactive game.',
    image: MelodyShaper,
    link: '/games/MelodyShaper/',
    duration: '10 minutes',
    difficulty: 'Easy',
    materials: ['Projector or shared screen', 'Speakers'],
    instructions: [
      'Project the "Melody Shaper" game for the class.',
      'Explain that students will hear a short melody and their goal is to draw its shape on the canvas.',
      'Click "Play Melody" to start a round.',
      'Have students come up to the projected screen (or use their own devices) to draw the shape.',
      'Click "Check My Drawing" to get feedback. Discuss the results as a class.'
    ]
  },
  {
    id: 'melopedes-creation-challenge',
    title: 'Melopede Caves: Melody Creation',
    description: 'Learn the basics of melody creation and composition by completing the Hot Cross Buns level in Melopede.',
    image: Melopedes,
    link: '/games/Melopedes',
    duration: '20 minutes',
    difficulty: 'Medium',
    materials: ['Computers/tablets with internet access', 'Individual student accounts'],
    instructions: [
      'Navigate to the Melopedes game on the Overture website.',
      'Explain that students must first complete the "Basics" and "Hot Cross Buns" levels.',
      'These levels teach the fundamentals of arranging notes into a melody.',
      'After completing the levels, students will be ready for composer mode in the next lesson.'
    ]
  },
  // Key Smashers removed - no longer available for students
  // {
  //   id: 'key-smashers-game',
  //   title: 'Key Smashers',
  //   description: 'A fun, fast-paced game to reinforce note recognition on the keyboard.',
  //   image: KeySmashers,
  //   duration: '10-15 minutes',
  //   difficulty: 'Easy',
  //   materials: ['Computers/tablets with internet access'],
  //   instructions: [
  //     'Direct students to the Key Smashers game.',
  //     'The goal is to correctly identify and "smash" the notes that appear on the screen.',
  //     'Encourage students to try to beat their high score and improve their speed.'
  //   ]
  // },
  {
    id: 'rocket-race-melody',
    title: 'Rocket Race (Meme Tunes & Composers)',
    description: 'Identify famous meme tunes or classical composers in this fast-paced racing game.',
    image: RocketRace,
    link: '/games/RocketRace',
    duration: '10-15 minutes',
    difficulty: 'Medium',
    materials: ['Projector or shared screen', 'Speakers'],
    instructions: [
      'Project the game Rocket Race for the class to see.',
      'Select the "Meme Tunes" or "Identify the Composer" mode.',
      'The class must listen carefully and call out the correct answer to make the rocket go faster.',
      'Compete as a class to get the best possible time.'
    ]
  },
  {
    id: 'melopedes-composer-mode',
    title: 'Melopedes: Composer Mode',
    description: 'Compose and save your own unique melodies using the bugs and compositional tools in Melopede.',
    image: Melopedes,
    link: '/games/Melopedes',
    duration: '20 minutes',
    difficulty: 'Hard',
    materials: ['Computers/tablets with internet access', 'Individual student accounts'],
    instructions: [
      'After completing the basic levels, students can now enter Composer Mode.',
      'Explain the different compositional tools: retrograde (backwards), inversion (upside down), repetition, and transposition (starting on a new note).',
      'Encourage students to drag in bugs, apply transformations, and create their own song.',
      'When they are happy with their melody, they must save it and give it a name.'
    ]
  },
  {
    id: 'tune-testers-7',
    title: 'Tune Testers: Melody vs. Rhythm',
    description: 'Use our new game, Tune Testers, to help students distinguish between melody, rhythm, and random noise!',
    image: TuneTesters,
    link: '/games/MelodyDiscovery/',
    duration: '15 minutes',
    difficulty: 'Easy',
    materials: ['Projector or shared screen', 'Speakers'],
    instructions: [
      'Project the "Tune Testers" game for the class to see.',
      'Explain the goal: "We\'re going to be Tune Testers! Your job is to listen carefully and decide what you hear."',
      'Start with the "Melody vs. Random" mode. Play a few rounds as a class, having students call out their answers.',
      'Discuss why one is a melody (it follows a pattern) and the other is just random notes.',
      'Switch to the "Melody vs. Rhythm" mode. Play a few rounds and have the class clap back both examples.',
      'Discuss the difference: Melody has different pitches (high and low), while rhythm is about the timing of notes.'
    ]
  },
  {
    id: 'step-skip-repeat-7',
    title: 'The "Step, Skip, or Repeat" Game',
    description: 'A fun, energetic game where students identify melodic movement by performing physical actions.',
    image: StepSkipRepeat,
    link: '/games/StepSkipRepeat/',
    duration: '10 minutes',
    difficulty: 'Easy',
    materials: ['Projector or shared screen', 'Speakers', 'Open space for students to move'],
    instructions: [
      'Project the "Step, Skip, or Repeat" game and make sure students have space to move.',
      'Explain the on-screen instructions: STAND UP for a step, JUMP for a skip, and SIT DOWN for a repeat.',
      'Click "Play Next Example" to play the sound for the class.',
      'Encourage all students to perform the action they think is correct.',
      'Click "Reveal Answer" to show the correct motion on screen and see if the class was right!'
    ]
  },
  {
    id: 'melody-shaper-7',
    title: 'Melody Shaper Game',
    description: 'Help students visualize the contour of a melody by drawing its shape in this interactive game.',
    image: MelodyShaper,
    link: '/games/MelodyShaper/',
    duration: '10 minutes',
    difficulty: 'Easy',
    materials: ['Projector or shared screen', 'Speakers'],
    instructions: [
      'Project the "Melody Shaper" game for the class.',
      'Explain that students will hear a short melody and their goal is to draw its shape on the canvas.',
      'Click "Play Melody" to start a round.',
      'Have students come up to the projected screen (or use their own devices) to draw the shape.',
      'Click "Check My Drawing" to get feedback. Discuss the results as a class.'
    ]
  },
  {
    id: 'melopedes-hot-cross-buns-7',
    title: 'Melopede Caves: Melody Creation',
    description: 'Learn the basics of melody creation and composition by completing the Hot Cross Buns level in Melopede.',
    image: Melopedes,
    link: '/games/Melopedes',
    duration: '20 minutes',
    difficulty: 'Medium',
    materials: ['Computers/tablets with internet access', 'Individual student accounts'],
    instructions: [
      'Navigate to the Melopedes game on the Overture website.',
      'Explain that students must first complete the "Basics" and "Hot Cross Buns" levels.',
      'These levels teach the fundamentals of arranging notes into a melody.',
      'After completing the levels, students will be ready for composer mode in the next lesson.'
    ]
  },
  {
    id: 'piano-clicker-side-quest-7',
    title: 'Piano Clicker',
    description: 'Play piano, earn cash, and unlock upgrades while learning about intervals, chords, and scales.',
    image: PianoClicker,
    link: '/games/PianoClicker',
    duration: '15-20 minutes',
    difficulty: 'Easy',
    materials: ['Computers/tablets with internet access', 'Individual student accounts'],
    instructions: [
      'Direct students to open the Piano Clicker game.',
      'Encourage them to explore the game freely.',
      'The goal is to click the piano to earn cash and purchase upgrades.',
      'Point out how the upgrades relate to musical concepts like intervals, chords, and scales.',
      'Challenge them to unlock as many items as they can in the time allotted.'
    ]
  },
  // Key Smashers removed - no longer available for students
  // {
  //   id: 'key-smashers-side-quest-7',
  //   title: 'Key Smashers',
  //   description: 'A fun, fast-paced game to reinforce note recognition on the keyboard.',
  //   image: KeySmashers,
  //   duration: '10-15 minutes',
  //   difficulty: 'Easy',
  //   materials: ['Computers/tablets with internet access'],
  //   instructions: [
  //     'Direct students to the Key Smashers game.',
  //     'The goal is to correctly identify and "smash" the notes that appear on the screen.',
  //     'Encourage students to try to beat their high score and improve their speed.'
  //   ]
  // },
  {
    id: 'rocket-race-melody-8',
    title: 'Rocket Race (Meme Tunes & Composers)',
    description: 'Identify famous meme tunes or classical composers in this fast-paced racing game.',
    image: RocketRace,
    link: '/games/RocketRace',
    duration: '10-15 minutes',
    difficulty: 'Medium',
    materials: ['Projector or shared screen', 'Speakers'],
    instructions: [
      'Project the game Rocket Race for the class to see.',
      'Select the "Meme Tunes" or "Identify the Composer" mode.',
      'The class must listen carefully and call out the correct answer to make the rocket go faster.',
      'Compete as a class to get the best possible time.'
    ]
  },
  {
    id: 'melopedes-composer-8',
    title: 'Melopedes: Composer Mode',
    description: 'Compose and save your own unique melodies using the bugs and compositional tools in Melopede.',
    image: Melopedes,
    link: '/games/Melopedes',
    duration: '20 minutes',
    difficulty: 'Hard',
    materials: ['Computers/tablets with internet access', 'Individual student accounts'],
    instructions: [
      'After completing the basic levels, students can now enter Composer Mode.',
      'Explain the different compositional tools: retrograde (backwards), inversion (upside down), repetition, and transposition (starting on a new note).',
      'Encourage students to drag in bugs, apply transformations, and create their own song.',
      'When they are happy with their melody, they must save it and give it a name.'
    ]
  },

  // Level 10 Activities - Dynamics
  {
    id: 'rocket-race-dynamics-10',
    title: 'Rocket Race: Dynamics',
    description: 'Race your rocket by correctly identifying musical dynamics.',
    image: RocketRace,
    link: '/games/RocketRace',
    duration: '10-15 minutes',
    difficulty: 'Medium',
    materials: ['Projector or shared screen', 'Speakers'],
    instructions: [
      'Project the game Rocket Race for the class to see.',
      'Select a mode related to dynamics.',
      'Explain that they need to listen carefully and have the class call out the correct answer to make the rocket go faster.',
      'Encourage them to try to beat their own high score.'
    ]
  },
  {
    id: 'flappy-muse-dynamics-10',
    title: 'Flappy Muse: Dynamics',
    description: 'Learn about dynamic markings (pp, p, mp, mf, f, ff) while guiding your muse through obstacles.',
    image: FlappyMuse,
    link: '/games/FlappyMuse',
    duration: '15 minutes',
    difficulty: 'Easy',
    materials: ['Student devices', 'Projector for demonstration'],
    instructions: [
      'Start as a whole class, explaining the meaning of pp, p, mp, mf, f, and ff.',
      'Use the game to demonstrate how each dynamic level affects gameplay.',
      'Have students pair up and play on their own devices.',
      'Challenge them to earn achievements by mastering the dynamic controls.'
    ]
  },
  {
    id: 'inside-orchestra-dynamics-10',
    title: 'Inside the Orchestra: Dynamic Game',
    description: 'An interactive game to practice identifying dynamics.',
    image: Rhythm101,
    link: 'https://activities.insidetheorchestra.org/dynamic-game/',
    duration: '10 minutes',
    difficulty: 'Easy',
    materials: ['Computers/tablets with internet access'],
    instructions: [
      'Navigate to the "Inside the Orchestra" dynamic game.',
      'Follow the on-screen instructions to play.',
      'Discuss the different dynamic levels presented in the game.'
    ]
  },
  {
    id: 'dynamics-rainstorm-10',
    title: 'Classroom Activity: Dynamics Rainstorm',
    description: 'Become a rainstorm using only your bodies to show how loud or soft the storm is.',
    image: Rhythm101,
    duration: '10 minutes',
    difficulty: 'Easy',
    materials: ['Open space for movement'],
    instructions: [
      'Teach each dynamic level with its matching body percussion: pp = finger rub, p = finger snaps, mp = light claps, mf = regular claps, f = thigh pats or light stomps, ff = stomp + clap.',
      'Practice each together while saying the Italian term out loud.',
      'Begin the storm at pp and gradually build to ff, using hand signals to indicate louder or softer dynamics.',
      'Once at peak ff, bring the storm back down to pp, then finish with silence or one final thunderclap.',
      'Repeat with a student as conductor or add a story for extra engagement.'
    ]
  },
  {
    id: 'spectrogram-side-quest-10',
    title: 'Side Quest: Overture Music Lab Spectrogram',
    description: 'Learn how sounds are made up of lots of other sounds using a spectrogram.',
    image: ChromeMusicLab,
    link: 'https://musiclab.chromeexperiments.com/Spectrogram/',
    duration: '10 minutes',
    difficulty: 'Medium',
    materials: ['Computers/tablets with internet access', 'Speakers'],
    instructions: [
      'Navigate to the Overture Music Lab Spectrogram.',
      'Experiment with different sounds (your voice, instruments, etc.).',
      'Observe how some frequencies are much louder (red) while some are softer (green).',
      'Discuss how this visualization represents the dynamics within a single sound.'
    ]
  },
  {
    id: 'soundgym-eq-side-quest-10',
    title: 'Side Quest: SoundGym EQ Game',
    description: 'Practice making different frequencies softer or louder to match the sound of a piece.',
    image: SoundGym,
    link: 'https://www.soundgym.co/playground/test?t=eq&lp=eq',
    duration: '15 minutes',
    difficulty: 'Hard',
    materials: ['Computers/tablets with internet access', 'Headphones recommended'],
    instructions: [
      'Navigate to the SoundGym EQ game.',
      'Listen to the original sound and the modified sound.',
      'Adjust the equalizer to make the frequencies softer or louder to match the target sound.',
      'This game connects what you saw in the spectrogram to how we can shape sound.'
    ]
  },

  // Level 11 - Genres
  {
    id: 'genres-overview-11',
    title: 'Genres Overview',
    description: 'Learn about different musical genres and their characteristics.',
    image: GenericActivity,
    duration: '20-25 minutes',
    difficulty: 'Medium',
    materials: ['Genre examples', 'Listening worksheets'],
    instructions: [
      'Listen to different musical examples',
      'Identify the genre',
      'Discuss the characteristics of the genre',
      'Practice singing or playing in the identified genre'
    ]
  },
  {
    id: 'listening-exercise-11',
    title: 'Listening Exercise',
    description: 'Listen to a variety of music and identify different genres.',
    image: GenericActivity,
    duration: '20-25 minutes',
    difficulty: 'Medium',
    materials: ['Music examples', 'Listening worksheets'],
    instructions: [
      'Listen to different musical examples',
      'Identify the genre',
      'Discuss the characteristics of the genre',
      'Practice singing or playing in the identified genre'
    ]
  },
  {
    id: 'artists-genres-11',
    title: 'Artists and Genres',
    description: 'Learn about different artists and their musical styles.',
    image: GenericActivity,
    duration: '20-25 minutes',
    difficulty: 'Medium',
    materials: ['Artist examples', 'Listening worksheets'],
    instructions: [
      'Listen to different musical examples',
      'Identify the artist',
      'Discuss the musical styles of the artist',
      'Practice singing or playing in the identified style'
    ]
  },
  {
    id: 'genre-quiz-score-11',
    title: 'Genre Quiz',
    description: 'Test your knowledge of different musical genres.',
    image: GenericActivity,
    duration: '15-20 minutes',
    difficulty: 'Medium',
    materials: ['Genre quiz questions', 'Whiteboard or paper for answers'],
    instructions: [
      'Answer the quiz questions',
      'Discuss the answers with the class',
      'Learn more about the genres'
    ]
  },

  // Level 12 - Greatest Hits
  {
    id: 'favorite-missions-12',
    title: 'Revisit Your Favorite Missions',
    description: 'Go back to your favorite activities and games from the program and make more progress.',
    image: FavoriteHits,
    duration: '20-30 minutes',
    difficulty: 'Easy',
    materials: ['Student devices'],
    instructions: [
      'As a class, discuss and decide on your favorite activities and games from your time in the program.',
      'Spend time playing those games and completing those activities again.',
      'Try to earn more achievements, beat your high scores, or create new things!'
    ]
  },
  {
    id: 'showcase-12',
    title: 'Student Showcase: Greatest Hits',
    description: "Share your favorite song you created with the class and explain why it's your favorite.",
    image: StudentSongs,
    duration: '15-20 minutes',
    difficulty: 'Easy',
    materials: ['Projector or large screen for dashboard view', 'Class speakers'],
    instructions: [
      "Each student will pick their favorite song they created during the program.",
      "Take turns sharing your song with the class.",
      "When you share, explain why it was your favorite song to create and what you like about it."
    ]
  },
  {
    id: 'rewards-12',
    title: 'Level Up & Rewards Ceremony',
    description: 'Celebrate your accomplishments with certificates and stickers!',
    image: TrophySongly,
    link: OvertureCertificate,
    duration: '10 minutes',
    difficulty: 'Easy',
    materials: ['Certificates of completion', 'Muse stickers', 'Dance music playlist'],
    instructions: [
      "It's time to celebrate all your hard work!",
      "As some fun dance music plays, each student will be awarded a certificate of completion.",
      "You can download and print the certificate to hand out.",
      "Give each student a physical sticker of their current muse.",
      "Congratulate them on becoming Level 1 Overture Musicians!"
    ]
  },
  {
    id: 'artists-genres-1-side-quest-12',
    title: 'Artists and Genres',
    description: 'Learn about different artists and their musical styles.',
    image: GenericActivity,
    duration: '20-25 minutes',
    difficulty: 'Medium',
    materials: ['Artist examples', 'Listening worksheets'],
    instructions: [
      'Listen to different musical examples',
      'Identify the artist',
      'Discuss the musical styles of the artist',
      'Practice singing or playing in the identified style'
    ]
  },
  {
    id: 'rhythm-rally-achievements-1-side-quest-12',
    title: 'Rhythm Rally: Digital Challenge',
    description: 'Online rhythm game where students compete to maintain rhythm patterns and earn achievements.',
    image: RhythmRally,
    link: '/games/RhythmRally',
    duration: '10 minutes',
    difficulty: 'Medium',
    materials: ['Devices (tablets/computers)', '3 students max per device'],
    instructions: [
      'Navigate to Rhythm Rally on the Overture website',
      'Assign devices with 3 students or fewer per device',
      'Explain objective: keep the rhythm going as long as possible',
      'Let students compete to see who can maintain rhythm longest',
      'Encourage students to help each other and celebrate achievements',
      'Monitor progress and provide encouragement for struggling students'
    ]
  },
  {
    id: 'beat-decoders-2-side-quest-12',
    title: 'Beat Decoders: Performance & Creation',
    description: 'Students play through Beat Decoders levels, learning to perform, modify, and create their own beats while earning achievements.',
    image: BeatDecoders,
    link: '/games/BeatDecoders3.0',
    duration: '20 minutes',
    difficulty: 'Medium',
    materials: ['Computers/tablets with internet access', 'Individual student accounts'],
    instructions: [
      'Have students login and navigate to Beat Decoders',
      'Explain: "Each level is an achievement!" Help them complete as many levels as possible',
      'Guide them through performing, modifying, and creating their own beats',
      'Encourage experimentation with different combinations and sounds',
      'Direct students to the songs tab to name each beat they created',
      'Celebrate their creations and achievements earned!'
    ]
  },
  {
    id: 'rhythm-rally-side-quest-2-side-quest-12',
    title: 'Rhythm Rally: Digital Challenge',
    description: 'Online rhythm game where students compete to maintain rhythm patterns and earn achievements.',
    image: RhythmRally,
    link: '/games/RhythmRally',
    duration: '10 minutes',
    difficulty: 'Medium',
    materials: ['Devices (tablets/computers)', '3 students max per device'],
    instructions: [
      'Navigate to Rhythm Rally on the Overture website',
      'Assign devices with 3 students or fewer per device',
      'Explain objective: keep the rhythm going as long as possible',
      'Let students compete to see who can maintain rhythm longest',
      'Encourage students to help each other and celebrate achievements',
      'Monitor progress and provide encouragement for struggling students'
    ]
  },
  {
    id: 'piano-clicker-3-side-quest-12',
    title: 'Piano Clicker',
    description: 'Play piano, earn cash, and unlock upgrades while learning about intervals, chords, and scales.',
    image: PianoClicker,
    link: '/games/PianoClicker',
    duration: '15-20 minutes',
    difficulty: 'Easy',
    materials: ['Computers/tablets with internet access', 'Individual student accounts'],
    instructions: [
      'Direct students to open the Piano Clicker game.',
      'Encourage them to explore the game freely.',
      'The goal is to click the piano to earn cash and purchase upgrades.',
      'Point out how the upgrades relate to musical concepts like intervals, chords, and scales.',
      'Challenge them to unlock as many items as they can in the time allotted.'
    ]
  },
  {
    id: 'piano-clicker-side-quest-4-side-quest-12',
    title: 'Piano Clicker',
    description: 'Play piano, earn cash, and unlock upgrades while learning about intervals, chords, and scales.',
    image: PianoClicker,
    link: '/games/PianoClicker',
    duration: '15-20 minutes',
    difficulty: 'Easy',
    materials: ['Computers/tablets with internet access', 'Individual student accounts'],
    instructions: [
      'Direct students to open the Piano Clicker game.',
      'Encourage them to explore the game freely.',
      'The goal is to click the piano to earn cash and purchase upgrades.',
      'Point out how the upgrades relate to musical concepts like intervals, chords, and scales.',
      'Challenge them to unlock as many items as they can in the time allotted.'
    ]
  },
  {
    id: 'tune-testers-7-side-quest-12',
    title: 'Tune Testers: Melody vs. Rhythm',
    description: 'Use our new game, Tune Testers, to help students distinguish between melody, rhythm, and random noise!',
    image: TuneTesters,
    link: '/games/MelodyDiscovery/',
    duration: '15 minutes',
    difficulty: 'Easy',
    materials: ['Projector or shared screen', 'Speakers'],
    instructions: [
      'Project the "Tune Testers" game for the class to see.',
      'Explain the goal: "We\'re going to be Tune Testers! Your job is to listen carefully and decide what you hear."',
      'Start with the "Melody vs. Random" mode. Play a few rounds as a class, having students call out their answers.',
      'Discuss why one is a melody (it follows a pattern) and the other is just random notes.',
      'Switch to the "Melody vs. Rhythm" mode. Play a few rounds and have the class clap back both examples.',
      'Discuss the difference: Melody has different pitches (high and low), while rhythm is about the timing of notes.'
    ]
  },
  {
    id: 'step-skip-repeat-7-side-quest-12',
    title: 'The "Step, Skip, or Repeat" Game',
    description: 'A fun, energetic game where students identify melodic movement by performing physical actions.',
    image: StepSkipRepeat,
    link: '/games/StepSkipRepeat/',
    duration: '10 minutes',
    difficulty: 'Easy',
    materials: ['Projector or shared screen', 'Speakers', 'Open space for students to move'],
    instructions: [
      'Project the "Step, Skip, or Repeat" game and make sure students have space to move.',
      'Explain the on-screen instructions: STAND UP for a step, JUMP for a skip, and SIT DOWN for a repeat.',
      'Click "Play Next Example" to play the sound for the class.',
      'Encourage all students to perform the action they think is correct.',
      'Click "Reveal Answer" to show the correct motion on screen and see if the class was right!'
    ]
  },
  {
    id: 'melody-shaper-7-side-quest-12',
    title: 'Melody Shaper Game',
    description: 'Help students visualize the contour of a melody by drawing its shape in this interactive game.',
    image: MelodyShaper,
    link: '/games/MelodyShaper/',
    duration: '10 minutes',
    difficulty: 'Easy',
    materials: ['Projector or shared screen', 'Speakers'],
    instructions: [
      'Project the "Melody Shaper" game for the class.',
      'Explain that students will hear a short melody and their goal is to draw its shape on the canvas.',
      'Click "Play Melody" to start a round.',
      'Have students come up to the projected screen (or use their own devices) to draw the shape.',
      'Click "Check My Drawing" to get feedback. Discuss the results as a class.'
    ]
  },
  {
    id: 'melopedes-hot-cross-buns-7-side-quest-12',
    title: 'Melopede Caves: Melody Creation',
    description: 'Learn the basics of melody creation and composition by completing the Hot Cross Buns level in Melopede.',
    image: Melopedes,
    link: '/games/Melopedes',
    duration: '20 minutes',
    difficulty: 'Medium',
    materials: ['Computers/tablets with internet access', 'Individual student accounts'],
    instructions: [
      'Navigate to the Melopedes game on the Overture website.',
      'Explain that students must first complete the "Basics" and "Hot Cross Buns" levels.',
      'These levels teach the fundamentals of arranging notes into a melody.',
      'After completing the levels, students will be ready for composer mode in the next lesson.'
    ]
  },
  {
    id: 'piano-clicker-side-quest-7-side-quest-12',
    title: 'Piano Clicker',
    description: 'Play piano, earn cash, and unlock upgrades while learning about intervals, chords, and scales.',
    image: PianoClicker,
    link: '/games/PianoClicker',
    duration: '15-20 minutes',
    difficulty: 'Easy',
    materials: ['Computers/tablets with internet access', 'Individual student accounts'],
    instructions: [
      'Direct students to open the Piano Clicker game.',
      'Encourage them to explore the game freely.',
      'The goal is to click the piano to earn cash and purchase upgrades.',
      'Point out how the upgrades relate to musical concepts like intervals, chords, and scales.',
      'Challenge them to unlock as many items as they can in the time allotted.'
    ]
  },
  {
    id: 'melopedes-composer-8-side-quest-12',
    title: 'Melopedes: Composer Mode',
    description: 'Compose and save your own unique melodies using the bugs and compositional tools in Melopede.',
    image: Melopedes,
    link: '/games/Melopedes',
    duration: '20 minutes',
    difficulty: 'Hard',
    materials: ['Computers/tablets with internet access', 'Individual student accounts'],
    instructions: [
      'After completing the basic levels, students can now enter Composer Mode.',
      'Explain the different compositional tools: retrograde (backwards), inversion (upside down), repetition, and transposition (starting on a new note).',
      'Encourage students to drag in bugs, apply transformations, and create their own song.',
      'When they are happy with their melody, they must save it and give it a name.'
    ]
  },
  {
    id: 'tune-testers-8-side-quest-side-quest-12',
    title: 'Tune Testers: Melody vs. Rhythm',
    description: 'Use our new game, Tune Testers, to help students distinguish between melody, rhythm, and random noise!',
    image: TuneTesters,
    link: '/games/MelodyDiscovery/',
    duration: '15 minutes',
    difficulty: 'Easy',
    materials: ['Projector or shared screen', 'Speakers'],
    instructions: [
      'Project the "Tune Testers" game for the class to see.',
      'Explain the goal: "We\'re going to be Tune Testers! Your job is to listen carefully and decide what you hear."',
      'Start with the "Melody vs. Random" mode. Play a few rounds as a class, having students call out their answers.',
      'Discuss why one is a melody (it follows a pattern) and the other is just random notes.',
      'Switch to the "Melody vs. Rhythm" mode. Play a few rounds and have the class clap back both examples.',
      'Discuss the difference: Melody has different pitches (high and low), while rhythm is about the timing of notes.'
    ]
  },
  {
    id: 'step-skip-repeat-8-side-quest-side-quest-12',
    title: 'The "Step, Skip, or Repeat" Game',
    description: 'A fun, energetic game where students identify melodic movement by performing physical actions.',
    image: StepSkipRepeat,
    link: '/games/StepSkipRepeat/',
    duration: '10 minutes',
    difficulty: 'Easy',
    materials: ['Projector or shared screen', 'Speakers', 'Open space for students to move'],
    instructions: [
      'Project the "Step, Skip, or Repeat" game and make sure students have space to move.',
      'Explain the on-screen instructions: STAND UP for a step, JUMP for a skip, and SIT DOWN for a repeat.',
      'Click "Play Next Example" to play the sound for the class.',
      'Encourage all students to perform the action they think is correct.',
      'Click "Reveal Answer" to show the correct motion on screen and see if the class was right!'
    ]
  },
  {
    id: 'react-attack-9-side-quest-12',
    title: 'React Attack: Form',
    description: 'Play React Attack to teach form by identifying section changes and assigning memes to sections.',
    image: ReactAttackTeacher,
    link: '/games/ReactAttackStudent',
    duration: '15-20 minutes',
    difficulty: 'Medium',
    materials: ['Student devices for student version', 'Teacher device for teacher version'],
    instructions: [
      'Students open the student version of React Attack, teachers open the teacher version.',
      'Play the game to teach form, first by identifying when the form changes.',
      'Then, by assigning memes to the form sections to define clear moods.',
    ]
  },
  {
    id: 'beat-decoders-side-quest-9-side-quest-12',
    title: 'Beat Decoders: Sectional Beats',
    description: 'Create a beat with two distinct sections in Beat Decoders.',
    image: BeatDecoders,
    link: '/games/BeatDecoders3.0',
    duration: '15 minutes',
    difficulty: 'Medium',
    materials: ['Computers/tablets with internet access', 'Individual student accounts'],
    instructions: [
        'Navigate to Beat Decoders.',
        'Create a beat that has two different-sounding sections.',
        'Think about making a verse and a chorus for your beat.',
        'Earn at least 2 achievements.'
    ]
  },
  {
    id: 'melopedes-side-quest-9-side-quest-12',
    title: 'Melopedes: Sectional Melodies',
    description: 'Create a melody with two distinct sections in Melopedes.',
    image: Melopedes,
    link: '/games/Melopedes',
    duration: '15 minutes',
    difficulty: 'Medium',
    materials: ['Computers/tablets with internet access', 'Individual student accounts'],
    instructions: [
        'Navigate to Melopedes.',
        'Create a melody that has two different-sounding sections.',
        'Use different bugs or patterns for each section.',
        'Earn at least 2 achievements.'
    ]
  },
  {
    id: 'flappy-muse-dynamics-10-side-quest-12',
    title: 'Flappy Muse: Dynamics',
    description: 'Learn about dynamic markings (pp, p, mp, mf, f, ff) while guiding your muse through obstacles.',
    image: FlappyMuse,
    link: '/games/FlappyMuse',
    duration: '15 minutes',
    difficulty: 'Easy',
    materials: ['Student devices', 'Projector for demonstration'],
    instructions: [
      'Start as a whole class, explaining the meaning of pp, p, mp, mf, f, and ff.',
      'Use the game to demonstrate how each dynamic level affects gameplay.',
      'Have students pair up and play on their own devices.',
      'Challenge them to earn achievements by mastering the dynamic controls.'
    ]
  },
  {
    id: 'dynamic-exercises-11-side-quest-12',
    title: 'Dynamics and Volume Control',
    description: 'Explore musical dynamics from pianissimo to fortissimo through interactive exercises.',
    image: Dynamics,
    duration: '20-25 minutes',
    difficulty: 'Easy',
    materials: ['Dynamic symbols cards', 'Music with clear dynamics'],
    instructions: [
      'Introduce dynamic markings: p, mp, mf, f',
      'Practice singing at different volume levels',
      'Use hand gestures to indicate dynamics',
      'Listen to classical music with clear dynamic changes',
      'Play "dynamics follow the leader" game',
      'Create a class song with dynamic changes'
    ]
  },
  {
    id: 'expression-performance-11-side-quest-12',
    title: 'Dynamics and Volume Control',
    description: 'Explore musical dynamics from pianissimo to fortissimo through interactive exercises.',
    image: Dynamics,
    duration: '20-25 minutes',
    difficulty: 'Easy',
    materials: ['Dynamic symbols cards', 'Music with clear dynamics'],
    instructions: [
      'Introduce dynamic markings: p, mp, mf, f',
      'Practice singing at different volume levels',
      'Use hand gestures to indicate dynamics',
      'Listen to classical music with clear dynamic changes',
      'Play "dynamics follow the leader" game',
      'Create a class song with dynamic changes'
    ]
  },
  {
    id: 'genre-quiz-score-12-side-quest-12',
    title: 'Genre Quiz',
    description: 'Test your knowledge of different musical genres.',
    image: GenericActivity,
    duration: '15-20 minutes',
    difficulty: 'Medium',
    materials: ['Genre quiz questions', 'Whiteboard or paper for answers'],
    instructions: [
      'Answer the quiz questions',
      'Discuss the answers with the class',
      'Learn more about the genres'
    ]
  },

  // Level 11 - Genres
  {
    id: 'what-is-genre-11',
    title: 'Introduction: What is a Music Genre?',
    description: 'Introduce the idea that different types of music belong to categories called "genres."',
    image: YoutubeImage,
    duration: '10 minutes',
    difficulty: 'Easy',
    materials: ['Projector and Speakers'],
    instructions: [
      'Ask students: "What kind of music do you like?"',
      'Explain that music is grouped into "families" called genres based on its elements: rhythm, harmony, instruments, melodies, dynamics, and form.',
      'Pull up child-appropriate videos on YouTube for 5 core genres: Classical (e.g., Beethoven), Jazz (e.g., Dave Brubeck), Rock (e.g., Queen), Pop (a modern hit), and Afro-Cuban/Folk.',
      'For each video, ask students to describe what they are hearing.',
      'Outcome: Students understand that genres are musical "families" with unique characteristics.'
    ]
  },
  {
    id: 'listening-imagination-11',
    title: 'Listening & Imagination Challenge',
    description: 'Use short listening exercises to help students identify musical differences.',
    image: YoutubeImage,
    duration: '15 minutes',
    difficulty: 'Easy',
    materials: ['Projector and Speakers'],
    instructions: [
      'Pull up a 1-minute video snippet for several different genres.',
      'After each, ask: "How does this music make you feel?", "What instruments do you hear?", "Would this music make you dance, relax, or feel excited?"',
      'Ask students to close their eyes while listening and imagine a scene or story that matches the music.',
      'Example prompt: "If this song were a movie, what would be happening?"',
      'Outcome: Students start recognizing tempo, instruments, and mood differences in music genres.'
    ]
  },
  {
    id: 'move-to-music-11',
    title: 'Move to the Music! Genre Movement Game',
    description: 'Connect music with movement through interactive activities.',
    image: YoutubeImage,
    duration: '10 minutes',
    difficulty: 'Easy',
    materials: ['A mix of music from different genres'],
    instructions: [
      'Play a mix of genres while students dance. When the music stops, students must freeze in a pose that matches the genre\'s mood.',
      'Outcome: Students engage physically and musically, reinforcing genre recognition through movement.'
    ]
  },
  {
    id: 'rocket-race-genres-11',
    title: 'Rocket Race: Genres',
    description: 'Race your rocket by correctly identifying musical genres.',
    image: RocketRace,
    link: '/games/RocketRace',
    duration: '10-15 minutes',
    difficulty: 'Medium',
    materials: ['Projector or shared screen', 'Speakers'],
    instructions: [
      'Project the game Rocket Race for the class to see.',
      'Select a mode related to genres.',
      'Explain that they need to listen carefully and have the class call out the correct answer to make the rocket go faster.',
      'Encourage them to try to beat their own high score.'
    ]
  },
  {
    id: 'real-or-ai-activity',
    title: 'Real or AI: Digital Literacy Brain Break',
    description: 'Short interactive break to build digital literacy: decide whether an image is real or AI-generated while resetting student focus.',
    image: RealOrAiImg,
    link: '/games/RealOrAI/index.html',
    duration: '5-10 minutes',
    difficulty: 'Easy',
    materials: ['Projector or shared screen'],
    instructions: [
      'Project Real or AI and explain: “We’re practicing digital literacy—can you spot which images are real and which are AI-generated?”',
      'Play several rounds quickly to keep it light and energizing.',
      'Briefly discuss clues students used (lighting, textures, artifacts) without deep-diving—this is a brain break.'
    ]
  },
  {
    id: 'suno-ai-genres-11',
    title: 'Suno AI: Genre Song Generation',
    description: 'Generate songs with student input for instruments, lyrics and genre.',
    image: SunoImage,
    link: 'https://suno.com/',
    duration: '15 minutes',
    difficulty: 'Medium',
    materials: ['Teacher account for Suno AI', 'Projector or shared screen'],
    instructions: [
      'Teachers make an account on Suno AI.',
      'Generate songs with student input for instruments, lyrics, and genre.',
      'Discuss which elements of music are contributing to the generated genre.',
      'Outcome: Students see how genre is a recipe for creating music.'
    ]
  },

  // Level 7 Activities
  {
    id: 'performance-preparation',
    title: 'Performance Preparation Workshop',
    description: 'Prepare students for musical performance through practice techniques and confidence-building exercises.',
    image: PerformancePrep,
    duration: '30-35 minutes',
    difficulty: 'Medium',
    materials: ['Performance space', 'Music stands', 'Recording device (optional)'],
    instructions: [
      'Discuss performance anxiety and coping strategies',
      'Practice proper posture and breathing',
      'Rehearse entering and exiting the performance space',
      'Work on eye contact and stage presence',
      'Practice starting and ending together',
      'Do a mock performance with peer feedback'
    ]
  },
];

// Mission ID to Activity ID mapping
export const missionActivityMap: Record<string, string> = {
  // Global promo mission mapping
  'winter-2026-promo': 'winter-2026-promo',
  // Level 1 - Rhythm Basics
  'kahoot-1': 'kahoot-rhythm-basics',
  'artists-genres-1': 'my-favorite-musicians-game',
  'rhythm-101-1': 'rhythm-101-basics',
  'rhythm-rally-achievements-1': 'rhythm-rally-game',
  'rhythm-puzzle-achievements-1': 'rhythm-puzzle-challenges',
  'reflect-and-progress-1': 'reflect-and-progress',
  
  // Level 2 - Advanced Rhythm
  'rhythm-warmup-2': 'rhythm-simon-says',
  'beat-making-intro-2': 'beat-making-intro',
  'beat-decoders-2': 'beat-decoders-challenge',
  'showcase-2': 'student-song-showcase',
  'rhythm-rally-side-quest-2': 'rhythm-rally-game',
  'rhythm-puzzle-side-quest-2': 'rhythm-puzzle-challenges',
  'rocket-race-side-quest-2': 'rocket-race-rhythm-feel',
  'reflect-and-progress-2': 'reflect-and-progress',
  
  // Level 3 - Harmony Basics
  'yeet-the-scoop-3': 'yeet-the-scoop-game',
  'scale-the-tower-3': 'scale-the-tower-game',
  'rocket-race-harmony-3': 'rocket-race-harmony',
  'chordstruction-3': 'chordstruction-game',
  'reflect-and-progress-3': 'reflect-and-progress',
  
  // Level 4 - Advanced Harmony
  'chord-progressions-4': 'harmony-theory-lesson',
  'voice-leading-4': 'harmony-theory-lesson',
  'rocket-race-harmony-4': 'rocket-race-harmony',
  'harmonidome-4': 'harmonidome-game',
  'showcase-4': 'student-song-showcase',
  'piano-clicker-4': 'piano-clicker-game',
  'yeet-the-scoop-side-quest-4': 'yeet-the-scoop-game',
  'reflect-and-progress-4': 'reflect-and-progress',
  
  // Level 5 - Instrument Families
  'instrument-families-5': 'instrument-families-exploration',
  'rocket-race-instruments-easy-5': 'rocket-race-instruments-easy',
  'instruments-pop-music-5': 'instruments-in-pop-music',
  'incredibox-5': 'incredibox-creation',
  'beat-decoders-instrument-focus-5': 'beat-decoders-instrument-focus',
  'reflect-and-progress-5': 'reflect-and-progress',

  // Level 6 - Instrumentation and Orchestration
  'rocket-race-instruments-easy-6': 'rocket-race-instruments-easy',
  'harmonidome-instruments-6': 'harmonidome-instruments-game',
  'showcase-6': 'student-song-showcase',
  'rocket-race-instruments-hard-6': 'rocket-race-instruments-hard',
  'incredibox-side-quest-6': 'incredibox-creation',
  'beat-decoders-instruments-6': 'beat-decoders-instrument-focus',
  'reflect-and-progress-6': 'reflect-and-progress',

  // Level 7 - Melody Basics
  'tune-testers-7': 'tune-testers-mission',
  'step-skip-repeat-7': 'step-skip-repeat-game',
  'melody-shaper-7': 'melody-shaper-game',
  'melopedes-hot-cross-buns-7': 'melopedes-creation-challenge',
  'piano-clicker-side-quest-7': 'piano-clicker-game',
  'reflect-and-progress-7': 'reflect-and-progress',

  // Level 8 - Advanced Melody
  'rocket-race-melody-8': 'rocket-race-melody',
  'tuna-8': 'tuna-game',
  'melopedes-composer-8': 'melopedes-composer-mode',
  'showcase-8': 'student-song-showcase',
  'piano-clicker-side-quest-8': 'piano-clicker-game',
  // 'key-smashers-side-quest-8': 'key-smashers-game', // Removed - no longer available
  'reflect-and-progress-8': 'reflect-and-progress',
  
  // Level 9 - Form Basics
  'what-is-musical-form-9': 'what-is-musical-form-9',
  'react-attack-9': 'react-attack-form-9',
  'chrome-music-lab-9': 'chrome-music-lab-form-9',
  'beat-decoders-side-quest-9': 'beat-decoders-form-9',
  'melopedes-side-quest-9': 'melopedes-form-9',
  'reflect-and-progress-9': 'reflect-and-progress',
  
  // Level 10 - Dynamics
  'rocket-race-dynamics-10': 'rocket-race-dynamics-10',
  'flappy-muse-dynamics-10': 'flappy-muse-dynamics-10',
  'inside-orchestra-dynamics-10': 'inside-orchestra-dynamics-10',
  'dynamics-rainstorm-10': 'dynamics-rainstorm-10',
  'spectrogram-side-quest-10': 'spectrogram-side-quest-10',
  'soundgym-eq-side-quest-10': 'soundgym-eq-side-quest-10',
  'reflect-and-progress-10': 'reflect-and-progress',

  // Level 11 - Genres
  'genres-overview-11': 'what-is-genre-11',
  'listening-exercise-11': 'listening-imagination-11',
  'artists-genres-11': 'move-to-music-11',
  'genre-quiz-score-11': 'rocket-race-genres-11',
  'real-or-ai-11': 'real-or-ai-activity',
  'suno-ai-11': 'suno-ai-genres-11',
  'reflect-and-progress-11': 'reflect-and-progress',

  // Level 12 - Greatest Hits
  'favorite-missions-12': 'favorite-missions-12',
  'showcase-12': 'showcase-12',
  'rewards-12': 'rewards-12',
  'reflect-and-progress-12': 'reflect-and-progress',
  'artists-genres-1-side-quest-12': 'my-favorite-musicians-game',
  'rhythm-rally-achievements-1-side-quest-12': 'rhythm-rally-game',
  'beat-decoders-2-side-quest-12': 'beat-decoders-challenge',
  'rhythm-rally-side-quest-2-side-quest-12': 'rhythm-rally-game',
  'piano-clicker-3-side-quest-12': 'piano-clicker-game',
  'piano-clicker-side-quest-4-side-quest-12': 'piano-clicker-game',
  'tune-testers-7-side-quest-12': 'tune-testers-mission',
  'step-skip-repeat-7-side-quest-12': 'step-skip-repeat-game',
  'melody-shaper-7-side-quest-12': 'melody-shaper-game',
  'melopedes-hot-cross-buns-7-side-quest-12': 'melopedes-creation-challenge',
  'piano-clicker-side-quest-7-side-quest-12': 'piano-clicker-game',
  'melopedes-composer-8-side-quest-12': 'melopedes-composer-mode',
  'tune-testers-8-side-quest-side-quest-12': 'tune-testers-mission',
  'step-skip-repeat-8-side-quest-side-quest-12': 'step-skip-repeat-game',
  'melody-shaper-8-side-quest-side-quest-12': 'melody-shaper-game',
  'piano-clicker-side-quest-8-side-quest-12': 'piano-clicker-game',
  'react-attack-9-side-quest-12': 'react-attack-form-9',
  'beat-decoders-side-quest-9-side-quest-12': 'beat-decoders-form-9',
  'melopedes-side-quest-9-side-quest-12': 'melopedes-form-9',
  'flappy-muse-dynamics-10-side-quest-12': 'flappy-muse-dynamics-10',
  'dynamic-exercises-11-side-quest-12': 'dynamics-lesson',
  'expression-performance-11-side-quest-12': 'dynamics-lesson',
  'genre-quiz-score-12-side-quest-12': 'genre-quiz-score-11'
};

// Utility functions
export const getActivityById = (id: string): Activity | undefined => {
  return activities.find(activity => activity.id === id);
};

export const getActivityForMission = (missionId: string): Activity | undefined => {
  const activityId = missionActivityMap[missionId];
  return activityId ? getActivityById(activityId) : undefined;
};

export const getMissionActivity = (missionId: string): Activity => {
  const activity = getActivityForMission(missionId);
  return activity || getActivityById('generic-completion')!;
}; 
